import type { AppID } from "@tiendanube/nube-sdk-internal-types";
import { generateSpanId } from "./utils.ts";
import type { TelemetryLog } from "./worker/handler/telemetry/telemetry.ts";

type ConsoleMethod = "log" | "info" | "warn" | "error" | "debug";

const CONSOLE_METHOD_TO_SEVERITY: Record<ConsoleMethod, TelemetryLog["type"]> =
	{
		log: "INFO",
		info: "INFO",
		warn: "WARN",
		error: "ERROR",
		debug: "DEBUG",
	};

{
	const OVERWRITTEN_CONSOLE_METHODS: Readonly<ConsoleMethod[]> = [
		// "log",
		// "info",
		// "warn",
		"error",
		// "debug",
	];

	const interceptedConsole: Partial<Console> = {};

	function stringifyArg(arg: unknown): string {
		if (arg instanceof Error) {
			return arg.stack ?? arg.message;
		}
		if (typeof arg === "object" && arg !== null) {
			try {
				return JSON.stringify(arg);
			} catch {
				return String(arg);
			}
		}
		return String(arg);
	}

	function extractErrorInfo(args: unknown[]): {
		message: string;
		stack: string | undefined;
	} {
		const firstError = args.find((arg): arg is Error => arg instanceof Error);
		const message = args.map(stringifyArg).join(" ");

		return {
			message,
			stack: firstError?.stack,
		};
	}

	for (const method of OVERWRITTEN_CONSOLE_METHODS) {
		interceptedConsole[method] = (...args: unknown[]) => {
			const { message, stack } = extractErrorInfo(args);

			const telemetry: TelemetryLog = {
				type: CONSOLE_METHOD_TO_SEVERITY[method],
				appId: self.__APP_DATA__.id,
				scriptUrl: self.__APP_DATA__.script,
				storeId: self.__INITIAL_STATE__?.store?.id ?? 0,
				storeName: self.__INITIAL_STATE__?.store?.name ?? "unknown",
				storeDomain: self.__INITIAL_STATE__?.store?.domain ?? "unknown",
				source: "overwritten-console-log",
				startTime: Date.now() * 1_000_000,
				message,
				stack,
			};
			self.postMessage(
				JSON.stringify({
					app: self.__APP_DATA__.id,
					type: "telemetry:logs",
					data: telemetry,
				}),
			);
		};
	}

	Object.assign(self.console, interceptedConsole);
}

export type FetchAuditMessage =
	| {
			type: "fetch:start";
			app: AppID;
			method: string;
			url: string;
			options?: RequestInit;
			requestId: string;
	  }
	| {
			type: "fetch:end";
			app: AppID;
			method: string;
			url: string;
			duration: number;
			success: true;
			status: number;
			requestId: string;
	  }
	| {
			type: "fetch:fail";
			app: AppID;
			method: string;
			url: string;
			duration: number;
			success: false;
			error: unknown;
			requestId: string;
	  };

{
	const originalFetch = fetch;

	self.fetch = async (
		input: RequestInfo | URL,
		init?: RequestInit,
	): Promise<Response> => {
		const method = init?.method ?? "GET";
		const url = typeof input === "string" ? input : input.toString();
		const startPerformanceTime = performance.now();
		const startTime = Math.round(
			(performance.timeOrigin + startPerformanceTime) * 1_000_000,
		); // Unix nanoseconds
		const app = self.__APP_DATA__.id;
		const requestId = crypto.randomUUID();
		const spanId = generateSpanId();

		// This message will be shown in the devtools
		const startMessage: FetchAuditMessage = {
			type: "fetch:start",
			app,
			method,
			url,
			options: init,
			requestId,
		};

		try {
			const response = await originalFetch(input, init);
			const duration = performance.now() - startPerformanceTime;

			// This message will be shown in the devtools
			const endMessage: FetchAuditMessage = {
				type: "fetch:end",
				app,
				method,
				url,
				duration,
				success: true,
				status: response.status,
				requestId,
			};

			self.postMessage(
				JSON.stringify({
					app: app,
					type: "telemetry:traces",
					data: {
						appId: app,
						scriptUrl: self.__APP_DATA__.script,
						storeId: self.__INITIAL_STATE__?.store?.id ?? 0,
						storeName: self.__INITIAL_STATE__?.store?.name ?? "unknown",
						storeDomain: self.__INITIAL_STATE__?.store?.domain ?? "unknown",
						source: "http-fetch",
						startTime,
						spanId,
						method,
						url,
						duration,
						success: true,
						status: response.status,
						requestId,
					},
				}),
			);

			return response;
		} catch (error) {
			const duration = performance.now() - startPerformanceTime;

			// This error message will be shown in the devtools
			const endMessage: FetchAuditMessage = {
				type: "fetch:fail",
				app,
				method,
				url,
				duration,
				success: false,
				error,
				requestId,
			};

			self.postMessage(
				JSON.stringify({
					app: app,
					type: "telemetry:traces",
					data: {
						appId: app,
						scriptUrl: self.__APP_DATA__.script,
						storeId: self.__INITIAL_STATE__?.store?.id ?? 0,
						storeName: self.__INITIAL_STATE__?.store?.name ?? "unknown",
						storeDomain: self.__INITIAL_STATE__?.store?.domain ?? "unknown",
						source: "http-fetch",
						startTime,
						spanId,
						method,
						url,
						duration,
						success: false,
						status: 0,
						error: error instanceof Error ? error.message : String(error),
						requestId,
					},
				}),
			);

			return Promise.reject(error);
		}
	};
}
